import { PREVIEW_AUDIO_SRC } from '@/features/runner/listening/listening-media'
import type { Section } from '@/lib/types'

/**
 * Full section definitions for the demo library cards (`src/lib/demo-data.ts`).
 *
 * Every card in `demoTests` must carry a complete `sections` tree in the exact
 * API wire shape the runner's definition mapper consumes
 * (`src/domain/runner/definition-mapper.ts`, RUNNER-CONTRACT §1.2-§1.3) - a
 * card without one sends the demo student to the "no longer available" page.
 * `demo-test-definitions.test.ts` pins that invariant for every card.
 *
 * Demo login runs the runner in preview mode, so:
 *  - Listening audio always resolves to the bundled local fixture
 *    (`resolvePartAudioUrl` returns `PREVIEW_AUDIO_SRC` in preview); the
 *    `file_path` values below point at the same fixture for honesty, and the
 *    `duration_seconds` metadata drives the derived section timer (§3.5);
 *  - Speaking uploads mint local `preview-<uuid>` media IDs with zero network;
 *  - nothing here may reference live media or live test IDs.
 */

const demoListeningSection: Section = {
  id: 'df-listening',
  title: 'Listening',
  type: 'Listening',
  description:
    '<p>You will hear four recordings. Answer the questions as you listen — each recording plays <strong>once only</strong>. After the final recording you have two minutes to check your answers.</p>',
  time_limit_minutes: 30,
  parts: [
    {
      id: 'df-l-p1',
      name: 'Part 1',
      title: 'Sports centre membership enquiry',
      order: 1,
      instructions: 'Listen to a telephone conversation between a caller and a receptionist at a community sports centre.',
      context_text: '<p>You will hear a woman telephoning the Riverside Community Sports Centre to ask about membership.</p>',
      media_resources: [
        {
          id: 'df-l-p1-audio',
          media_type: 'audio',
          file_path: PREVIEW_AUDIO_SRC,
          duration_seconds: 431,
          description: 'Part 1 recording',
        },
      ],
      question_groups: [
        {
          id: 'df-l-p1-g1',
          instructions: 'Complete the notes below. Write <strong>NO MORE THAN TWO WORDS AND/OR A NUMBER</strong> for each answer.',
          group_order: 1,
          questions: [
            {
              id: 'df-l-q1',
              number: 1,
              text: `<p><strong>Riverside Community Sports Centre — membership notes</strong></p>
                <p>Type of membership requested: {{1}} membership</p>
                <p>Cost per month: £{{2}}</p>
                <p>Includes free use of the {{3}} every weekday morning</p>
                <p>Caller's surname: {{4}}</p>
                <p>Start date: 4 {{5}}</p>`,
              type: 'fill_in_blank',
              points_value: 5,
              type_data: {
                case_sensitive: false,
                blanks: [
                  { id: 'df-l-q1-b1', accepted_answers: ['off-peak', 'off peak'] },
                  { id: 'df-l-q1-b2', accepted_answers: ['28.50', '28.5'] },
                  { id: 'df-l-q1-b3', accepted_answers: ['swimming pool', 'pool'] },
                  { id: 'df-l-q1-b4', accepted_answers: ['Tremblay'] },
                  { id: 'df-l-q1-b5', accepted_answers: ['September'] },
                ],
              },
            },
          ],
        },
        {
          id: 'df-l-p1-g2',
          instructions: 'Write <strong>ONE WORD ONLY</strong> for the answer.',
          group_order: 2,
          questions: [
            {
              id: 'df-l-q2',
              number: 1,
              text: 'The caller should collect her membership card from the ________ desk.',
              type: 'single_answer',
              points_value: 1,
              type_data: { accepted_answers: ['reception'], case_sensitive: false },
            },
          ],
        },
      ],
    },
    {
      id: 'df-l-p2',
      name: 'Part 2',
      title: 'Harbourside Arts Festival',
      order: 2,
      instructions: 'Listen to a festival organiser giving information about this year’s Harbourside Arts Festival.',
      media_resources: [
        {
          id: 'df-l-p2-audio',
          media_type: 'audio',
          file_path: PREVIEW_AUDIO_SRC,
          duration_seconds: 456,
          description: 'Part 2 recording',
        },
      ],
      question_groups: [
        {
          id: 'df-l-p2-g1',
          instructions: 'Choose the correct letter, <strong>A</strong>, <strong>B</strong> or <strong>C</strong>.',
          group_order: 1,
          questions: [
            {
              id: 'df-l-q3',
              number: 1,
              text: 'This year the festival will open with',
              type: 'single_choice',
              points_value: 1,
              type_data: {
                options: ['a firework display over the harbour', 'a parade of decorated fishing boats', 'an open-air concert on the quay'],
                correct_option: 'a parade of decorated fishing boats',
              },
            },
            {
              id: 'df-l-q4',
              number: 2,
              text: 'Tickets for the sculpture workshop are limited because',
              type: 'single_choice',
              points_value: 1,
              type_data: {
                options: ['the studio space is small', 'materials have to be ordered in advance', 'each participant works with a mentor'],
                correct_option: 'the studio space is small',
              },
            },
            {
              id: 'df-l-q5',
              number: 3,
              text: 'Visitors arriving by car are advised to',
              type: 'single_choice',
              points_value: 1,
              type_data: {
                options: ['park at the railway station', 'use the park-and-ride service', 'arrive before ten o’clock'],
                correct_option: 'use the park-and-ride service',
              },
            },
          ],
        },
        {
          id: 'df-l-p2-g2',
          instructions: 'Choose <strong>TWO</strong> letters, <strong>A–E</strong>.',
          group_order: 2,
          questions: [
            {
              id: 'df-l-q6',
              number: 1,
              text: 'Which TWO changes have been made to the festival programme this year?',
              type: 'multiple_choice',
              points_value: 2,
              type_data: {
                max_selections: 2,
                options: [
                  'The poetry tent has moved to the old customs house',
                  'The food market now runs every evening',
                  'A childrens’ storytelling stage has been added',
                  'The photography exhibition is free of charge',
                  'The closing concert has been extended by an hour',
                ],
                correct_options: [
                  'The poetry tent has moved to the old customs house',
                  'A childrens’ storytelling stage has been added',
                ],
              },
            },
          ],
        },
      ],
    },
    {
      id: 'df-l-p3',
      name: 'Part 3',
      title: 'Tutorial: fieldwork project',
      order: 3,
      instructions: 'Listen to two geography students, Priya and Daniel, discussing their coastal fieldwork project with their tutor.',
      media_resources: [
        {
          id: 'df-l-p3-audio',
          media_type: 'audio',
          file_path: PREVIEW_AUDIO_SRC,
          duration_seconds: 419,
          description: 'Part 3 recording',
        },
      ],
      question_groups: [
        {
          id: 'df-l-p3-g1',
          instructions: 'Choose the correct letter, <strong>A</strong>, <strong>B</strong> or <strong>C</strong>.',
          group_order: 1,
          questions: [
            {
              id: 'df-l-q7',
              number: 1,
              text: 'Priya and Daniel agree that their pilot survey was',
              type: 'single_choice',
              points_value: 1,
              type_data: {
                options: ['too short to be reliable', 'carried out at the wrong time of day', 'based on a sample that was too narrow'],
                correct_option: 'based on a sample that was too narrow',
              },
            },
            {
              id: 'df-l-q8',
              number: 2,
              text: 'What does the tutor recommend they do next?',
              type: 'single_choice',
              points_value: 1,
              type_data: {
                options: ['repeat the measurements at low tide', 'compare their data with last year’s cohort', 'reduce the number of survey sites'],
                correct_option: 'compare their data with last year’s cohort',
              },
            },
          ],
        },
        {
          id: 'df-l-p3-g2',
          instructions: 'Complete the sentences below. Write <strong>NO MORE THAN TWO WORDS</strong> for each answer.',
          group_order: 2,
          questions: [
            {
              id: 'df-l-q9',
              number: 1,
              text: 'Complete the sentences about the students’ revised plan.',
              type: 'sentence_completion',
              points_value: 3,
              type_data: {
                sentences: [
                  { sentence: 'The students will collect sediment samples from the {answer} of the estuary.', correct_answer: 'northern shore', word_limit: 2 },
                  { sentence: 'Daniel will borrow a {answer} from the department’s equipment store.', correct_answer: 'current meter', word_limit: 2 },
                  { sentence: 'Their final report must include a section on {answer}.', correct_answer: 'data limitations', word_limit: 2 },
                ],
              },
            },
          ],
        },
      ],
    },
    {
      id: 'df-l-p4',
      name: 'Part 4',
      title: 'Lecture: urban beekeeping',
      order: 4,
      instructions: 'Listen to part of a lecture about beekeeping in cities.',
      media_resources: [
        {
          id: 'df-l-p4-audio',
          media_type: 'audio',
          file_path: PREVIEW_AUDIO_SRC,
          duration_seconds: 442,
          description: 'Part 4 recording',
        },
      ],
      question_groups: [
        {
          id: 'df-l-p4-g1',
          instructions: 'Complete the notes below. Write <strong>ONE WORD ONLY</strong> for each answer.',
          group_order: 1,
          questions: [
            {
              id: 'df-l-q10',
              number: 1,
              text: `<p><strong>Urban beekeeping</strong></p>
                <p>City hives often out-produce rural ones because gardens offer a longer {{1}} season.</p>
                <p>Rooftop sites protect colonies from {{2}} spraying used on farmland.</p>
                <p>Main risk: hives placed too close together compete for {{3}}.</p>
                <p>Speaker's advice: register every colony with the city {{4}}.</p>`,
              type: 'fill_in_blank',
              points_value: 4,
              type_data: {
                case_sensitive: false,
                blanks: [
                  { id: 'df-l-q10-b1', accepted_answers: ['flowering'] },
                  { id: 'df-l-q10-b2', accepted_answers: ['pesticide'] },
                  { id: 'df-l-q10-b3', accepted_answers: ['forage', 'food'] },
                  { id: 'df-l-q10-b4', accepted_answers: ['register', 'council'] },
                ],
              },
            },
          ],
        },
      ],
    },
  ],
}

const demoReadingSection: Section = {
  id: 'df-reading',
  title: 'Reading',
  type: 'Reading',
  description:
    '<p>Read three passages and answer all questions. You can highlight text and add notes in the passage pane. There are no extra marks for finishing early.</p>',
  time_limit_minutes: 60,
  parts: [
    {
      id: 'df-r-p1',
      name: 'Passage 1',
      title: 'The hidden life of urban soils',
      order: 1,
      instructions: 'Read the passage and answer Questions 1–7.',
      context_text: `<p><strong>The hidden life of urban soils</strong></p>
        <p>Beneath pavements and park lawns, city soils host a density of microbial life that routinely surprises the researchers who sample it. Far from being sterile fill, the ground under a typical residential street can contain thousands of bacterial species in a single gram, many of them found nowhere else in the surrounding region.</p>
        <p>Urban soils are young in geological terms. Most were assembled in the last two centuries from builders' rubble, imported topsoil and whatever the previous land use left behind. This patchwork origin gives them a character quite unlike farmland: two samples taken metres apart may differ more than samples taken from fields a hundred kilometres apart.</p>
        <p>That variability has consequences for the trees planted above. A study of street trees in Rotterdam found that saplings of the same species and age grew at strikingly different rates depending on the compaction and biology of the soil pocket each occupied. Where fungal networks were intact, growth was faster and drought damage rarer.</p>
        <p>City authorities have begun to respond. Several northern European municipalities now specify 'structural soils' — engineered mixes of stone and organic matter — under new pavements, and a few require contractors to inoculate planting pits with fungi harvested from mature parkland nearby.</p>
        <p>The lesson researchers draw is modest but firm: the underground city deserves the same deliberate design as the visible one. What is buried carelessly today constrains what can grow for decades.</p>`,
      question_groups: [
        {
          id: 'df-r-p1-g1',
          instructions: 'Do the following statements agree with the information given in the passage? Choose <strong>TRUE</strong>, <strong>FALSE</strong> or <strong>NOT GIVEN</strong>.',
          group_order: 1,
          questions: [
            {
              id: 'df-r-q1',
              number: 1,
              text: 'Urban soils generally contain less microbial life than rural soils.',
              type: 'true_false_not_given',
              points_value: 1,
              type_data: { correct_answer: 'false' },
            },
            {
              id: 'df-r-q2',
              number: 2,
              text: 'Soil samples taken close together in cities can vary more than distant farmland samples.',
              type: 'true_false_not_given',
              points_value: 1,
              type_data: { correct_answer: 'true' },
            },
            {
              id: 'df-r-q3',
              number: 3,
              text: 'The Rotterdam study was funded by the city government.',
              type: 'true_false_not_given',
              points_value: 1,
              type_data: { correct_answer: 'not given' },
            },
          ],
        },
        {
          id: 'df-r-p1-g2',
          instructions: 'Complete the summary below. Write <strong>NO MORE THAN TWO WORDS</strong> from the passage for each answer.',
          group_order: 2,
          questions: [
            {
              id: 'df-r-q4',
              number: 1,
              text: `<p>Most city soils were built up over the last two centuries from materials such as {{1}} and imported topsoil. Trees grow faster where {{2}} are intact, and some municipalities now place engineered {{3}} under new pavements.</p>`,
              type: 'fill_in_blank',
              points_value: 3,
              type_data: {
                case_sensitive: false,
                blanks: [
                  { id: 'df-r-q4-b1', accepted_answers: ["builders' rubble", 'rubble'] },
                  { id: 'df-r-q4-b2', accepted_answers: ['fungal networks'] },
                  { id: 'df-r-q4-b3', accepted_answers: ['structural soils'] },
                ],
              },
            },
          ],
        },
      ],
    },
    {
      id: 'df-r-p2',
      name: 'Passage 2',
      title: 'Navigating by the stars',
      order: 2,
      instructions: 'Read the passage and answer Questions 7–14.',
      context_text: `<p><strong>Navigating by the stars: the revival of celestial wayfinding</strong></p>
        <p><b>A</b> For most of maritime history, a navigator's instruments were the horizon, the heavens and a trained memory. The Pacific voyaging traditions of Polynesia carried this art to its height: navigators held mental star maps of more than a hundred rising and setting points, and could hold a course for weeks without a single written note.</p>
        <p><b>B</b> The arrival of satellite positioning seemed to make such skills obsolete. By the 1990s, celestial navigation had been dropped from most naval curricula, and the sextant became a collector's item rather than a working tool.</p>
        <p><b>C</b> The revival began, unexpectedly, with questions of resilience. Militaries and merchant fleets recognised that a system dependent on a handful of satellites has a single point of failure, whether from technical fault or deliberate interference. Several academies quietly reinstated star navigation as a required subject.</p>
        <p><b>D</b> A second current of revival is cultural. Voyaging societies in Hawai‘i and across Oceania have rebuilt canoes and retrained navigators, treating wayfinding not as nostalgia but as living knowledge. Their ocean crossings, made without instruments, have drawn worldwide attention.</p>
        <p><b>E</b> Researchers who study these navigators report cognitive effects that extend beyond the sea: sustained attention, rich spatial memory and a practised calm in uncertainty. Whatever satellites can do, the discipline of reading the sky appears to train the mind in ways no receiver can replace.</p>`,
      question_groups: [
        {
          id: 'df-r-p2-g1',
          instructions: 'The passage has five paragraphs, <strong>A–E</strong>. Choose the correct heading for each paragraph from the list of headings below.',
          group_order: 1,
          questions: [
            {
              id: 'df-r-q5',
              number: 1,
              text: 'Choose the correct heading for each paragraph.',
              type: 'matching_headings',
              points_value: 5,
              type_data: {
                paragraphs: ['A', 'B', 'C', 'D', 'E'],
                headings: [
                  'Mental benefits observed in practitioners',
                  'A skill made redundant by technology',
                  'The peak of instrument-free navigation',
                  'Commercial pressures on modern shipping',
                  'Security concerns prompt a return',
                  'Reclaiming navigation as heritage',
                  'The design of the modern sextant',
                ],
                correct_matches: { A: '2', B: '1', C: '4', D: '5', E: '0' },
              },
            },
          ],
        },
        {
          id: 'df-r-p2-g2',
          instructions: 'Complete the sentences below. Write <strong>NO MORE THAN TWO WORDS</strong> from the passage for each answer.',
          group_order: 2,
          questions: [
            {
              id: 'df-r-q6',
              number: 1,
              text: 'Complete the sentences.',
              type: 'sentence_completion',
              points_value: 3,
              type_data: {
                sentences: [
                  { sentence: 'Polynesian navigators memorised the rising and setting points on mental {answer}.', correct_answer: 'star maps', word_limit: 2 },
                  { sentence: 'Satellite systems were judged to have a single point of {answer}.', correct_answer: 'failure', word_limit: 1 },
                  { sentence: 'Voyaging societies regard wayfinding as {answer} rather than nostalgia.', correct_answer: 'living knowledge', word_limit: 2 },
                ],
              },
            },
          ],
        },
      ],
    },
    {
      id: 'df-r-p3',
      name: 'Passage 3',
      title: 'The economics of attention',
      order: 3,
      instructions: 'Read the passage and answer Questions 15–20.',
      context_text: `<p><strong>The economics of attention</strong></p>
        <p><b>A</b> When economists first described attention as a scarce resource in the early 1970s, the observation was largely theoretical. Information was still expensive to produce and distribute; the bottleneck sat with publishers, not audiences.</p>
        <p><b>B</b> The economics inverted within a generation. Once distribution became effectively free, the scarce input was no longer the message but the minutes of the person receiving it. Whole industries reorganised around capturing and holding those minutes.</p>
        <p><b>C</b> Measurement followed the money. Engagement metrics — clicks, watch time, session length — became the accounting units of the attention economy, prized because they could be counted, not because they captured value to the viewer.</p>
        <p><b>D</b> Critics argue that this measurement choice quietly redefined success. Content that interrupts, provokes or unsettles scores well on engagement even when audiences report regretting the time spent, a divergence researchers call the satisfaction gap.</p>
        <p><b>E</b> A small counter-movement now experiments with different accounting: apps that report time well spent, subscriptions that remove the incentive to maximise sessions, and design certifications for products that end interactions cleanly.</p>
        <p><b>F</b> Whether such alternatives can scale remains open. What is no longer disputed is the premise: in a world of abundant information, the design of what we pay attention to has become an economic question of the first order.</p>`,
      question_groups: [
        {
          id: 'df-r-p3-g1',
          instructions: 'The passage has six paragraphs, <strong>A–F</strong>. Which paragraph contains the following information?',
          group_order: 1,
          questions: [
            {
              id: 'df-r-q7',
              number: 1,
              text: 'Match each statement with the paragraph that contains it.',
              type: 'matching_information',
              points_value: 4,
              type_data: {
                statements: {
                  '1': 'a mismatch between measured engagement and how audiences feel afterwards',
                  '2': 'examples of products designed around alternatives to engagement metrics',
                  '3': 'the historical period when attention scarcity was first described',
                  '4': 'why countable measures were adopted as the industry standard',
                },
                paragraphs: ['A', 'B', 'C', 'D', 'E', 'F'],
                correct_matches: { '1': 'D', '2': 'E', '3': 'A', '4': 'C' },
              },
            },
          ],
        },
        {
          id: 'df-r-p3-g2',
          instructions: 'Choose the correct letter, <strong>A</strong>, <strong>B</strong>, <strong>C</strong> or <strong>D</strong>.',
          group_order: 2,
          questions: [
            {
              id: 'df-r-q8',
              number: 1,
              text: 'According to the passage, distribution becoming cheap meant that',
              type: 'single_choice',
              points_value: 1,
              type_data: {
                options: [
                  'publishers regained control of the bottleneck',
                  'audience time became the scarce input',
                  'the quality of information declined',
                  'economists abandoned the theory of scarcity',
                ],
                correct_option: 'audience time became the scarce input',
              },
            },
            {
              id: 'df-r-q9',
              number: 2,
              text: 'The writer’s overall conclusion is that',
              type: 'single_choice',
              points_value: 1,
              type_data: {
                options: [
                  'engagement metrics accurately reflect viewer value',
                  'the counter-movement has already succeeded at scale',
                  'the direction of attention is now a first-order economic question',
                  'information abundance has made design irrelevant',
                ],
                correct_option: 'the direction of attention is now a first-order economic question',
              },
            },
          ],
        },
      ],
    },
  ],
}

const demoFullWritingSection: Section = {
  id: 'df-writing',
  title: 'Writing',
  type: 'Writing',
  description:
    '<p>Complete both tasks. Task 2 carries twice the weight of Task 1. Word counts are guidance — you will not be stopped from submitting, but short answers lose marks.</p>',
  time_limit_minutes: 60,
  parts: [
    // RUNNER-CONTRACT §5.1: exactly one part whose FIRST question group holds
    // both essays; Task 1 vs Task 2 is the question order inside that group.
    {
      id: 'df-w-p1',
      name: 'Writing tasks',
      order: 1,
      media_resources: [
        {
          id: 'df-w-media-1',
          media_type: 'image',
          file_path: '/mocktest/media/df-w-media-1/',
          description: 'Bar chart showing household recycling rates in four countries, 2005–2025',
        },
      ],
      question_groups: [
        {
          id: 'df-w-g1',
          group_order: 1,
          questions: [
            {
              id: 'df-w-q1',
              number: 1,
              text: '<p>The chart below shows household recycling rates in four countries between 2005 and 2025.</p><p>Summarise the information by selecting and reporting the main features, and make comparisons where relevant.</p>',
              type: 'essay',
              points_value: 9,
              type_data: { min_words: 150, topic_type: 'chart_description' },
            },
            {
              id: 'df-w-q2',
              number: 2,
              text: '<p>Some people believe that public money spent on hosting international sporting events would be better invested in local sports facilities. To what extent do you agree or disagree?</p><p>Give reasons for your answer and include any relevant examples from your own knowledge or experience.</p>',
              type: 'essay',
              points_value: 9,
              type_data: { min_words: 250, topic_type: 'opinion_essay' },
            },
          ],
        },
      ],
    },
  ],
}

const demoSpeakingSection: Section = {
  id: 'df-speaking',
  title: 'Speaking',
  type: 'Speaking',
  description:
    '<p>Three parts, recorded one question at a time. You can re-record an answer until you move on. Part 2 gives you one minute to prepare notes before you speak.</p>',
  time_limit_minutes: 14,
  parts: [
    {
      id: 'df-s-p1',
      name: 'Part 1',
      title: 'Introduction and interview',
      order: 1,
      duration_minutes: 4,
      instructions: '<p>The examiner asks general questions about yourself and familiar topics. Answer naturally — there is no preparation time in this part.</p>',
      question_groups: [
        {
          id: 'df-s-p1-g1',
          group_order: 1,
          questions: [
            {
              id: 'df-s-q1',
              number: 1,
              text: 'Let’s talk about where you live. What do you like most about your neighbourhood?',
              type: 'interview_style',
              points_value: 1,
              type_data: { speaking_time: 30 },
            },
            {
              id: 'df-s-q2',
              number: 2,
              text: 'Do you prefer to study or work in the morning or in the evening? Why?',
              type: 'interview_style',
              points_value: 1,
              type_data: { speaking_time: 30 },
            },
            {
              id: 'df-s-q3',
              number: 3,
              text: 'How often do you cook at home, and what do you usually make?',
              type: 'interview_style',
              points_value: 1,
              type_data: { speaking_time: 30 },
            },
          ],
        },
      ],
    },
    {
      id: 'df-s-p2',
      name: 'Part 2',
      title: 'Individual long turn',
      order: 2,
      duration_minutes: 4,
      instructions: '<p>You have one minute to prepare. Then speak for one to two minutes. You can make notes while you prepare.</p>',
      question_groups: [
        {
          id: 'df-s-p2-g1',
          group_order: 1,
          questions: [
            {
              id: 'df-s-q4',
              number: 1,
              text: 'Describe a place in your town or city that has changed significantly since you first knew it.',
              type: 'long_turn',
              points_value: 1,
              type_data: {
                preparation_time: 60,
                speaking_time: 120,
                prompt_points: [
                  'where the place is',
                  'what it was like when you first knew it',
                  'how it has changed',
                  'and explain how you feel about these changes',
                ],
              },
            },
          ],
        },
      ],
    },
    {
      id: 'df-s-p3',
      name: 'Part 3',
      title: 'Two-way discussion',
      order: 3,
      duration_minutes: 5,
      instructions: '<p>The examiner asks broader questions connected to the Part 2 topic. Develop your answers with reasons and examples.</p>',
      question_groups: [
        {
          id: 'df-s-p3-g1',
          group_order: 1,
          questions: [
            {
              id: 'df-s-q5',
              number: 1,
              text: 'Why do you think some people dislike changes to the area where they live?',
              type: 'interview_style',
              points_value: 1,
              type_data: { speaking_time: 45 },
            },
            {
              id: 'df-s-q6',
              number: 2,
              text: 'Should residents be consulted before major redevelopment projects go ahead? Why or why not?',
              type: 'interview_style',
              points_value: 1,
              type_data: { speaking_time: 45 },
            },
            {
              id: 'df-s-q7',
              number: 3,
              text: 'How might cities balance preserving historic areas with the need for new housing?',
              type: 'interview_style',
              points_value: 1,
              type_data: { speaking_time: 45 },
            },
          ],
        },
      ],
    },
  ],
}

/** Academic Mock 04 — the full four-skill demo simulation (`demo-full`). */
export const demoFullSections: Section[] = [
  demoListeningSection,
  demoReadingSection,
  demoFullWritingSection,
  demoSpeakingSection,
]

/** Writing Coherence Diagnostic — the two-task Writing demo (`demo-writing`). */
export const demoWritingSections: Section[] = [
  {
    id: 'dw-writing',
    title: 'Writing',
    type: 'Writing',
    description:
      '<p>Two tasks focused on coherence and cohesion: organise your ideas into clear paragraphs and link them explicitly. Aim to spend about 20 minutes on Task 1 and 40 minutes on Task 2.</p>',
    time_limit_minutes: 60,
    parts: [
      {
        id: 'dw-w-p1',
        name: 'Writing tasks',
        order: 1,
        question_groups: [
          {
            id: 'dw-w-g1',
            group_order: 1,
            questions: [
              {
                id: 'dw-w-q1',
                number: 1,
                text: '<p>The table below summarises how students at a language school rated four aspects of their course (teaching, materials, facilities and social programme) at the start and end of one term.</p><p>Summarise the information by selecting and reporting the main features, and make comparisons where relevant.</p><table><thead><tr><th>Aspect</th><th>Start of term</th><th>End of term</th></tr></thead><tbody><tr><td>Teaching</td><td>4.1</td><td>4.5</td></tr><tr><td>Materials</td><td>3.8</td><td>3.9</td></tr><tr><td>Facilities</td><td>3.2</td><td>2.9</td></tr><tr><td>Social programme</td><td>2.7</td><td>3.8</td></tr></tbody></table>',
                type: 'essay',
                points_value: 9,
                type_data: { min_words: 150, topic_type: 'data_description' },
              },
              {
                id: 'dw-w-q2',
                number: 2,
                text: '<p>In many countries, people increasingly work from home rather than commuting to a workplace. Do the advantages of this trend outweigh the disadvantages?</p><p>Give reasons for your answer and include any relevant examples from your own knowledge or experience. Organise your ideas into clearly linked paragraphs.</p>',
                type: 'essay',
                points_value: 9,
                type_data: { min_words: 250, topic_type: 'discussion_essay' },
              },
            ],
          },
        ],
      },
    ],
  },
]
